// Single home for every assumption about the Identity-Api user payload shape.
// The search/invite contracts aren't pinned to exact casing yet, so all reading of
// user fields and all building of requests funnels through here — when real staging
// data lands, this is the only file that needs adjusting.

import { roleLabel } from './roleCatalog';

// ScopeType is the integer enum owned by Identity-Api (`enum ScopeType { School, Group }`,
// so School = 0, Group = 1). These same values are sent both as the org-MFA `entityType`
// and as the invite `scopeType`. Confirmed against Identity-Api source.
export const SCOPE_TYPE = {
  school: 0,
  group: 1,
};

// Sortable columns map to the field name the API is expected to sort by. The table
// degrades gracefully if the backend ignores an unknown sortBy, so a wrong guess here
// is cosmetic, not breaking.
// TODO: confirm the sortBy field names accepted by /users/search.
export const SORT_FIELDS = {
  name: 'name',
  lastLogin: 'lastLogin',
};

const firstDefined = (source, keys) => {
  if (!source) return undefined;
  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null) return source[key];
  }
  return undefined;
};

const readRoleName = (role) => {
  if (typeof role === 'string') return roleLabel(role);
  return roleLabel(firstDefined(role, ['code', 'Code', 'roleCode', 'RoleCode']), {
    displayName: firstDefined(role, ['displayName', 'DisplayName']),
    name: firstDefined(role, ['name', 'roleName', 'Name', 'RoleName']),
  });
};

const readScopeSchoolName = (scope) => {
  if (typeof scope === 'string') return scope;
  return firstDefined(scope, [
    'schoolName',
    'SchoolName',
    'entityName',
    'EntityName',
    'name',
    'Name',
  ]);
};

const INACTIVE_STATES = new Set(['inactive', 'deleted', 'suspended', 'disabled', 'archived']);

// A MfaPolicy row counts as org-level enforcement when it is Required/enforced and not
// narrowed to a single role, tolerating the several casings the API might use.
const ORG_MFA_ENFORCED_SIGNALS = ['require', 'enforce', 'mandatory'];
// MfaEnforcementLevel enum: Optional = 0, Required = 1 (serialised as an integer). Any level
// at or above Required counts as enforced.
const MFA_ENFORCEMENT_REQUIRED = 1;

const isEnforcedOrgMfaPolicy = (policy) => {
  if (!policy || typeof policy !== 'object') return false;

  // Primary signal: the typed enforcementLevel (Required = 1). Numeric enum comes over the wire
  // as an int; also accept a string form defensively.
  const enforcement = firstDefined(policy, ['enforcementLevel', 'EnforcementLevel']);
  const enforcementRequired =
    typeof enforcement === 'number'
      ? enforcement >= MFA_ENFORCEMENT_REQUIRED
      : ORG_MFA_ENFORCED_SIGNALS.some((signal) =>
          String(enforcement ?? '').toLowerCase().includes(signal)
        );

  const requiredFlag = firstDefined(policy, [
    'isRequired',
    'IsRequired',
    'required',
    'Required',
    'isEnforced',
    'IsEnforced',
  ]);
  const mode = String(
    firstDefined(policy, [
      'enforcement',
      'Enforcement',
      'mode',
      'Mode',
      'policyType',
      'PolicyType',
      'type',
      'Type',
      'status',
      'Status',
    ]) || ''
  ).toLowerCase();
  const isRequired =
    enforcementRequired ||
    requiredFlag === true ||
    ORG_MFA_ENFORCED_SIGNALS.some((signal) => mode.includes(signal));
  if (!isRequired) return false;

  const roleFilter = firstDefined(policy, [
    'roleCode',
    'RoleCode',
    'roleCodes',
    'RoleCodes',
    'roleId',
    'RoleId',
    'role',
    'Role',
    'roleName',
    'RoleName',
  ]);
  const hasRoleFilter = Array.isArray(roleFilter)
    ? roleFilter.length > 0
    : roleFilter !== undefined && roleFilter !== null && roleFilter !== '';
  return !hasRoleFilter;
};

// Reduces the untyped MfaPolicy/by-entity response to a single on/off for the org-level
// "Require MFA" toggle: on when the entity has at least one Required, role-agnostic policy.
// Returns undefined when the response shape can't be read, so the caller can disable the
// toggle rather than render a misleading OFF.
export function interpretOrgMfaEnrolled(response) {
  const policies = Array.isArray(response)
    ? response
    : firstDefined(response, ['policies', 'Policies', 'items', 'Items', 'data', 'Data']);
  if (!Array.isArray(policies)) return undefined;
  return policies.some(isEnforcedOrgMfaPolicy);
}

// True when a policy carries no role filter (an entity-wide org policy).
function isRoleAgnostic(policy) {
  if (!policy || typeof policy !== 'object') return false;
  const roleFilter = firstDefined(policy, [
    'roleCode', 'RoleCode', 'roleCodes', 'RoleCodes',
    'roleId', 'RoleId', 'role', 'Role', 'roleName', 'RoleName',
  ]);
  const hasRoleFilter = Array.isArray(roleFilter)
    ? roleFilter.length > 0
    : roleFilter !== undefined && roleFilter !== null && roleFilter !== '';
  return !hasRoleFilter;
}

// The entity's OWN org-level MFA level, distinguishing an explicit opt-out from no policy:
//   'required' — a Required role-agnostic policy (org MFA on)
//   'optional' — a role-agnostic policy that isn't Required (an explicit opt-out override)
//   'none'     — no entity-wide policy of its own (so it inherits any parent scope)
// Returns undefined when the response can't be read. Lets a school distinguish "I opted out"
// (Optional, which overrides the parent group) from "I inherit the group".
export function interpretOrgMfaLevel(response) {
  const policies = Array.isArray(response)
    ? response
    : firstDefined(response, ['policies', 'Policies', 'items', 'Items', 'data', 'Data']);
  if (!Array.isArray(policies)) return undefined;
  const roleAgnostic = policies.filter(isRoleAgnostic);
  if (roleAgnostic.length === 0) return 'none';
  return policies.some(isEnforcedOrgMfaPolicy) ? 'required' : 'optional';
}

// Reads one raw user item into the flat shape the table renders, tolerating the
// several casings the API might use for each field.
export function mapUser(raw) {
  const roles = (firstDefined(raw, ['roleNames', 'roles', 'RoleNames', 'Roles']) || [])
    .map(readRoleName)
    .filter(Boolean);
  // Identity pairs each role name with its catalogue code, in the same order. An older
  // Identity sends names only; the access-level summary then maps names back to codes.
  const apiRoleCodes = firstDefined(raw, ['roleCodes', 'RoleCodes']);
  const roleCodes =
    Array.isArray(apiRoleCodes) && apiRoleCodes.length === roles.length ? apiRoleCodes : [];

  // Identity scopes carry entity ids only (no names — names live in the Welbee domain). The
  // group account view resolves these school ids to names via a map it already holds.
  const scopes = firstDefined(raw, ['scopes', 'Scopes']) || [];
  const schoolNames = scopes.map(readScopeSchoolName).filter(Boolean);
  const schoolIds = scopes.flatMap((scope) => firstDefined(scope, ['schoolIds', 'SchoolIds']) || []);
  const groupIds = scopes.flatMap((scope) => firstDefined(scope, ['groupIds', 'GroupIds']) || []);

  const statusText = firstDefined(raw, ['status', 'Status', 'state', 'State']);
  const isDeleted = firstDefined(raw, ['isDeleted', 'IsDeleted']) === true;
  const isActiveFlag = firstDefined(raw, ['isActive', 'IsActive']);
  const isInactive =
    isDeleted ||
    isActiveFlag === false ||
    (typeof statusText === 'string' && INACTIVE_STATES.has(statusText.toLowerCase()));

  return {
    id: firstDefined(raw, ['userId', 'id', 'UserId', 'Id']),
    name:
      firstDefined(raw, ['name', 'fullName', 'displayName', 'Name', 'FullName', 'DisplayName']) ||
      [firstDefined(raw, ['firstName', 'FirstName']), firstDefined(raw, ['lastName', 'LastName'])]
        .filter(Boolean)
        .join(' ') ||
      '',
    email: firstDefined(raw, ['email', 'emailAddress', 'Email', 'EmailAddress']) || '',
    lastLogin: firstDefined(raw, [
      'lastLogin',
      'lastLoginAt',
      'lastLoginDate',
      'lastSignInAt',
      'LastLogin',
      'LastLoginAt',
      'LastLoginDate',
    ]),
    // The discrete Identity UserStatus (Active/PendingVerification/Inactive/Suspended/
    // Deleted) plus whether the pending set-password invite has expired — drives the
    // account-lifecycle pill + status-aware action once the grid is filtered off Active.
    status: firstDefined(raw, ['status', 'Status', 'state', 'State']),
    inviteExpired: firstDefined(raw, ['inviteExpired', 'InviteExpired']) === true,
    roles,
    roleCodes,
    schoolNames,
    schoolIds,
    groupIds,
    isInactive,
    raw,
  };
}

// Presentational last-login. Null/absent reads as "Never" rather than an epoch or crash.
export function formatLastLogin(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Builds the /users/search request. The scope decides which id filter is sent; paging
// is 1-based to match getUserActivity. Sort/search fields are only included when set so
// the backend sees a clean request. The SearchUsersRequest field names below are confirmed
// against Identity-Api; the 1-based paging base and exact sortBy field names still merit a
// staging check.
export function buildSearchRequest({
  scope,
  schoolId,
  groupId,
  page = 1,
  pageSize = 10,
  sortBy,
  sortDescending,
  searchTerm,
  status,
}) {
  const request = { page, pageSize };

  if (scope === 'group') {
    if (groupId !== undefined && groupId !== null) request.groupIds = [groupId];
  } else if (schoolId !== undefined && schoolId !== null) {
    request.schoolIds = [schoolId];
  }

  // A concrete UserStatus filters the list; the "All" sentinel (or nothing) is omitted so
  // the backend returns every status.
  if (status && status !== 'All') request.status = status;

  if (sortBy) {
    request.sortBy = sortBy;
    request.sortDescending = Boolean(sortDescending);
  }

  const trimmed = (searchTerm || '').trim();
  if (trimmed) request.searchTerm = trimmed;

  return request;
}

// The Admin-Api provisioning userRole is derived from context, mirroring the People
// Management flow: a group with no specific school (i.e. all schools) or several schools is
// a Group Director; a group narrowed to exactly one school, or any single-school scope, is a
// Head Teacher.
export const USER_ROLE = {
  groupDirector: 'GroupDirector',
  headTeacher: 'HeadTeacher',
};

function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function uniqueNumbers(values) {
  return [...new Set((values || []).map(toNumberOrNull).filter((v) => v !== null))];
}

// True when the operator's site selection covers the whole group: nothing chosen (⇒ all),
// or every available school chosen.
function isAllSites(siteIds, allSiteIds) {
  const selected = uniqueNumbers(siteIds);
  const all = uniqueNumbers(allSiteIds);
  if (selected.length === 0) return true;
  return all.length > 0 && selected.length === all.length && all.every((id) => selected.includes(id));
}

// Builds the Identity invitation payload (createInvitationData equivalent). The MFA toggle
// drives both enableMfa and the preferred factor (TOTP when enabled).
export function buildInvitationData({ email, firstName, lastName, enableMfa = false, invitedBy }) {
  return {
    email: (email || '').trim(),
    firstName: (firstName || '').trim(),
    lastName: (lastName || '').trim(),
    phoneNumber: '',
    invitedBy: invitedBy || 'Super Admin',
    inviteReason: 'Access management invitation',
    userType: 0,
    enableMfa: Boolean(enableMfa),
    preferredMfaFactor: enableMfa ? 1 : 0,
  };
}

// Builds the Identity scopeAssignments. A school scope is a single SCHOOL assignment; a
// group scope pairs a GROUP assignment with a SCHOOL assignment over the chosen schools
// (all schools ⇒ wildcard access over the full list), matching the People Management flow.
export function buildScopeAssignments({
  scope,
  schoolId,
  groupId,
  siteIds = [],
  allSiteIds = [],
  parentGroupId,
}) {
  if (scope !== 'group') {
    const id = toNumberOrNull(schoolId);
    // When the school belongs to a MAT, stamp the parent group on the assignment so login
    // resolves the group's MFA (and other group policies) for this school-only user —
    // without it, group-level MFA never reaches a school-scoped user.
    return [
      {
        scopeType: SCOPE_TYPE.school,
        entityIds: id !== null ? [id] : [],
        isWildcardAccess: false,
        parentGroupId: toNumberOrNull(parentGroupId),
      },
    ];
  }

  const groupNum = toNumberOrNull(groupId);
  const wildcard = isAllSites(siteIds, allSiteIds);
  const schoolIds = wildcard ? uniqueNumbers(allSiteIds) : uniqueNumbers(siteIds);

  const assignments = [
    {
      scopeType: SCOPE_TYPE.group,
      entityIds: groupNum !== null ? [groupNum] : [],
      isWildcardAccess: wildcard,
      parentGroupId: null,
    },
  ];
  if (schoolIds.length > 0) {
    assignments.push({
      scopeType: SCOPE_TYPE.school,
      entityIds: schoolIds,
      isWildcardAccess: wildcard,
      parentGroupId: groupNum,
    });
  }
  return assignments;
}

// Builds the Admin-Api provisioning payload (UserProvisioning/provision). `sites` is the
// chosen school ids ([] means "all schools" for a group), `access` the numeric role ids,
// and `userRole` is derived from how many schools the grant covers.
export function buildProvisioningData({
  userId,
  email,
  firstName,
  lastName,
  scope,
  groupId,
  schoolId,
  siteIds = [],
  allSiteIds = [],
  roleIds = [],
  schoolStaffId = 0,
}) {
  const isGroup = scope === 'group';
  const sites = isGroup
    ? isAllSites(siteIds, allSiteIds)
      ? []
      : uniqueNumbers(siteIds)
    : uniqueNumbers([schoolId]);

  const userRole =
    isGroup && sites.length !== 1 ? USER_ROLE.groupDirector : USER_ROLE.headTeacher;

  return {
    userId,
    email: (email || '').trim(),
    firstName: (firstName || '').trim(),
    lastName: (lastName || '').trim(),
    userRole,
    schoolStaffId: schoolStaffId || 0,
    sites,
    access: roleIds,
    matGroupId: isGroup ? toNumberOrNull(groupId) || 0 : 0,
  };
}
